import React, { useContext, useState } from 'react';
import { Alert, Button, Checkbox, Flex, Form, List, Modal, Select, Spin, message } from 'antd';
import TextArea from 'antd/es/input/TextArea';

import { FormContainer } from '@leedpulse-monorepo/components';
import AuthenticationContext from '../../contexts/auth/AuthenticationContext';
import SubscriptionsContext from '../../contexts/data/SubscriptionsContext';

import ScenarioElementType, { scenarioElementTypeAsString } from '../../../domain/enums/scenario/ScenarioElementType';
import enhanceScenarioAI from '../../../domain/use_cases/scenarios/enhanceScenarioAI';

const MAX_LENGTH_TEXT_AREA = 6000;

/**
 * Detected fix from AI enhancement
 */
interface DetectedFix {
  issue: string;
  origin: string;
  resolution: string;
}

/**
 * Result of the AI enhancement
 */
interface EnhancedScenarioResult {
  type: ScenarioElementType;
  value?: string;
  detectedFixes?: DetectedFix[];
}

/**
 * Props for ScenarioEnhanceModal
 */
interface ScenarioEnhanceModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** The scenario element type to enhance */
  elementType: ScenarioElementType | null;
  /** The current value of the element to enhance */
  currentValue: string;
  /** The scenario ID */
  scenarioId: string;
  /** Callback when modal is closed */
  onClose: () => void;
  /** Callback when enhanced value is applied */
  onApply: (type: ScenarioElementType, value: string) => void;
}

/**
 * Modal component for enhancing scenario elements with AI
 */
const ScenarioEnhanceModal: React.FC<ScenarioEnhanceModalProps> = ({
  open,
  elementType,
  currentValue,
  scenarioId,
  onClose,
  onApply,
}) => {
  const authContext = useContext(AuthenticationContext);
  const subscriptionsContext = useContext(SubscriptionsContext);

  // Local state for the enhancement flow
  const [isGlobalEnhancing, setIsGlobalEnhancing] = useState<boolean>(false);
  const [apiKeySubscriptionSelected, setApiKeySubscriptionSelected] = useState<string | undefined>(undefined);
  const [enhancedScenario, setEnhancedScenario] = useState<EnhancedScenarioResult | null>(null);
  const [isEnhancing, setIsEnhancing] = useState<boolean>(false);

  // Reset state when modal is closed or element type changes
  // Ajuste pendant le rendu (pattern « ajuster l'etat quand une prop change » de
  // la doc React) : dans un effet, ce recalcul coutait un second rendu.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (!open) {
      setEnhancedScenario(null);
      setIsGlobalEnhancing(false);
      setApiKeySubscriptionSelected(undefined);
      setIsEnhancing(false);
    }
  }

  /**
   * Call the AI enhancement API
   */
  const handleEnhance = async () => {
    if (!authContext.authentication || !elementType) return;

    setIsEnhancing(true);

    try {
      const res = await enhanceScenarioAI(
        {
          sTextToEnhance: currentValue,
          sTextType: elementType.toString(),
          sOwner: authContext.authentication.id,
          sScenarioId: scenarioId,
          sIdApiKey: apiKeySubscriptionSelected,
        },
        authContext.authentication
      );

      setEnhancedScenario({
        type: elementType,
        value: res.enhanced_prompt,
        detectedFixes: res.detected_fixes as DetectedFix[],
      });
    } catch {
      message.error("Erreur lors de l'amélioration du scénario");
    } finally {
      setIsEnhancing(false);
    }
  };

  /**
   * Apply the enhanced value and close the modal
   */
  const handleApply = () => {
    if (enhancedScenario?.type && enhancedScenario?.value !== undefined) {
      onApply(enhancedScenario.type, enhancedScenario.value);
    }
    onClose();
  };

  /**
   * Update the enhanced value in the textarea
   */
  const handleEnhancedValueChange = (value: string) => {
    if (enhancedScenario) {
      setEnhancedScenario({
        ...enhancedScenario,
        value,
      });
    }
  };

  /**
   * Render the result view after enhancement
   */
  const renderResultView = () => {
    if (!enhancedScenario) return null;

    const hasNoChanges = enhancedScenario.value === currentValue;

    return (
      <>
        {hasNoChanges ? (
          <Alert description="Aucune correction détectée." type="success" />
        ) : (
          <Alert
            description="Les corrections peuvent ne pas être pertinentes, une vérification manuelle est nécessaire."
            type="error"
          />
        )}

        <Form layout="vertical" style={{ margin: '24px 0' }}>
          <FormContainer twoColumnsMax>
            <Form.Item label="Texte original">
              <TextArea
                maxLength={MAX_LENGTH_TEXT_AREA}
                value={currentValue}
                onChange={() => null}
                style={{ height: '250px', width: '100%' }}
                showCount
                readOnly
              />
            </Form.Item>
            <Form.Item label="Texte boosté à l'IA">
              <TextArea
                maxLength={MAX_LENGTH_TEXT_AREA}
                value={enhancedScenario?.value}
                onChange={(e) => handleEnhancedValueChange(e.target.value)}
                style={{ height: '250px', width: '100%' }}
                showCount
              />
            </Form.Item>
          </FormContainer>

          {enhancedScenario?.detectedFixes && enhancedScenario.detectedFixes.length > 0 && (
            <Alert
              message="Problèmes détectés"
              description={
                <List
                  className="list-detected-fixes"
                  style={{ maxHeight: '250px', overflowY: 'auto' }}
                  dataSource={enhancedScenario.detectedFixes}
                  renderItem={(item: DetectedFix) => (
                    <List.Item>
                      <b>Problème :</b> {item.issue} <br />
                      <b>Origine :</b>{' '}
                      {item.origin === 'local'
                        ? `${scenarioElementTypeAsString(enhancedScenario.type)} du scénario`
                        : 'Compatibilité avec le prompt global'}{' '}
                      <br />
                      <b>Résolution :</b> {item.resolution}
                    </List.Item>
                  )}
                />
              }
              type="warning"
              showIcon
            />
          )}
        </Form>

        <Flex justify="end" gap="8px">
          <Button type="primary" onClick={onClose}>
            Annuler
          </Button>
          <Button type="primary" onClick={handleApply}>
            Appliquer
          </Button>
        </Flex>
      </>
    );
  };

  /**
   * Render the configuration view before enhancement
   */
  const renderConfigView = () => {
    if (!elementType) {
      return <p>Aucun élément du scénario à améliorer</p>;
    }

    return (
      <>
        <Form layout="vertical">
          <FormContainer twoColumnsMax>
            <Form.Item label=" ">
              <Checkbox
                checked={isGlobalEnhancing}
                onChange={(e) => setIsGlobalEnhancing(e.target.checked)}
              >
                Prendre en compte le contexte global (Prompt complet)
              </Checkbox>
            </Form.Item>
            {isGlobalEnhancing && (
              <Form.Item label="Abonnement">
                <Select
                  showSearch
                  filterOption={(input, option) =>
                    option?.props?.children?.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  placeholder="Sélectionner..."
                  onChange={(value) => setApiKeySubscriptionSelected(value)}
                  value={apiKeySubscriptionSelected}
                >
                  {subscriptionsContext.items.map((sub) => (
                    <Select.Option key={sub.id} value={sub.apiKey}>
                      {sub.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            )}
          </FormContainer>
        </Form>

        <Flex justify="end" gap="8px">
          <Button type="primary" onClick={onClose}>
            Annuler
          </Button>
          <Button
            type="primary"
            onClick={handleEnhance}
            disabled={isEnhancing || (isGlobalEnhancing && !apiKeySubscriptionSelected)}
            icon={isEnhancing ? <Spin size="small" /> : undefined}
          >
            Améliorer
          </Button>
        </Flex>
      </>
    );
  };

  return (
    <Modal
      title={`Améliorer le scénario : ${scenarioElementTypeAsString(elementType || ScenarioElementType.description)}`}
      open={open}
      onCancel={onClose}
      footer={null}
      width={1000}
    >
      {enhancedScenario ? renderResultView() : renderConfigView()}
    </Modal>
  );
};

export default ScenarioEnhanceModal;

